import url from 'url';
import { stringify } from 'querystring';
import {
  ARTICLE_ROUTE_TYPE,
  EDITION_SPA_REDIRECT_DISABLED_PAGE_TYPES
} from '../constants';
import { locationAttrs } from './dom';
import {memoize, get, toLower} from 'lodash';

/*
 ***********************************************
 * All types of redirects, on client and server *
 ***********************************************
 */

const parseUrl = memoize(url.parse);

/*
 * `shouldRedirectOnServer` and `getRedirectUrlForServer`
 * ======================================================
 * This redirect is a special case: it's when we're on an article page
 * page via the id route (ex. http://www.refinery29.com/12345).
 *
 * In these cases we want to redirect to the basename route (ex.
 * http://www.refinery29.com/an-article).
 *
 * Ideally this redirect would occur earlier in the request lifecycle,
 * like our other types of redirects. But setting up our routing layer
 * to do this would require a carefully coordinated migration. Easier for
 * now to do it in Rosetta.
 */
export const shouldRedirectOnServer = (req, store) => {
  const {
    isPreviewPage,
    analytics: { url_full }
  } = store.getState();

  return !!(url_full
    && req.pageConfig.type === ARTICLE_ROUTE_TYPE
    && !isPreviewPage
    && (parseUrl(url_full).pathname !== req.path)
  );
};

export const getRedirectUrlForServer = (req, store) => {
  const { host, path: pathFromUrlFull } =
    parseUrl(store.getState().analytics.url_full);
  const queryString = stringify(req.query);
  return 'https://' + host + pathWithQueryString(pathFromUrlFull, queryString);
};

/*
 * `shouldRedirectOnClient` and `getRedirectPathForClient`
 * ======================================================
 * These handle the case where we make an AJAX request on the
 * client and are 301 redirected to a new URL via nginx.
 * If we do not reload the page here, then the browser bar will not
 * be properly updated to the URL provided by the 301.
 *
 * We are unable to intercept the 301 here via axios because axios
 * actually has no way of intercepting the 301 - the browser will just
 * continue the redirect and then eventually return a 200 from the new URL.
 *
 * This case could happen in two places: 1) we link to a URL that has
 * a util redirect set via the redirect tool 2) we link to a URL that is a sites url
 * (.com) but should now be an editions url (en-us).
 * These 301 redirects occur on the nginx level via our application-routing code.
 *
 * An example to recreate:
 * - I visit refinery29.com
 * - I go to the top navigation and click 67 Percent (/plus-size-clothing)
 * - An AJAX request is made to /plus-size-clothing?json=true
 *    - A 301 redirect is made to /en-us/plus-size-clothing?json=true via nginx
 *    - A 200 is returned by /en-us/plus-sizing-clothing?json=true
 *    - We see that /plus-size-clothing (the current location.pathname)
 *      does not match /en-us/plus-size-clothing (url_full) and force a
 *      full page reload to update the browser URL.
 *
 * A few things to note:
 * - We only want to do this check on page types that have the correct value for url_full
 *   (archives, contributors, and static always return a hardcoded value).
 * - Redirects from .uk => .com or .de => .com (like in the case of editions) will cause a
 *   DOMAIN_CHANGED_ERROR in axios where we also reload. They will not follow this code path.
 */

export const shouldRedirectOnClient = (apiResponse, fetchType) => {
  const pageType = toLower(get(apiResponse, 'result.page_data.page_type'));
  const urlFull = get(apiResponse, 'result.page_data.url_full');

  return !!(fetchType === 'fetchingNewPage'
    && pageType
    && urlFull
    && !EDITION_SPA_REDIRECT_DISABLED_PAGE_TYPES.includes(pageType)
    && (parseUrl(urlFull).path !== locationAttrs(window).pathname)
  );
};

export const getRedirectPathForClient = (apiResponse) => {
  const { path: pathFromUrlFull } = parseUrl(
    apiResponse.result.page_data.url_full
  );
  // remove question mark
  const queryString = locationAttrs(window).search.replace(/^\?/, '');
  return pathWithQueryString(pathFromUrlFull, queryString);
};

function pathWithQueryString(pathFromUrlFull, queryString) {
  /*
   * Note: we don't care about the query string from the url_full,
   * because our current convention is to not include query strings
   * in that API field, whether on bridge or Foundry. If that ever
   * changes we should merge the two query strings together.
   */

  return queryString === ''
    ? pathFromUrlFull
    : pathFromUrlFull + '?' + queryString;
}
